import { FieldValidator, useField, useFormikContext } from 'formik';
import { forwardRef } from 'react';

import { composeValidators, required } from 'shared/utils/form';

import { TextField, TextFieldProps } from '../TextField';
import { getIsInvalid } from '../common';

export interface FormTextFieldProps extends Omit<TextFieldProps, 'inputProps'> {
  /** Name of the field. */
  name?: string;

  validate?: FieldValidator;

  /** @deprecated Use `name`, `validate` or `required` instead. */
  fieldProps?: {
    name: string;
    validate?: FieldValidator;
    required?: boolean;
  };

  /** @deprecated Use normal props instead. */
  inputProps?: TextFieldProps;
}

export const FormTextField = forwardRef<HTMLInputElement, FormTextFieldProps>(
  (
    {
      fieldProps,
      inputProps = {},
      name = fieldProps?.name,
      required: isRequired = fieldProps?.required ?? inputProps?.required,
      validate,
      ...props
    },
    ref,
  ) => {
    const validation = composeValidators(
      isRequired ? required : undefined,
      validate ?? fieldProps?.validate,
    );

    const [field, meta] = useField({
      name: name as string,
      validate: validation,
    });

    const form = useFormikContext();

    const hasError = getIsInvalid({ meta, form });
    const message = hasError && meta.error;

    return (
      <TextField
        ref={ref}
        variant="outlined"
        {...props}
        {...inputProps}
        name={name}
        value={field.value ?? ''}
        onChange={field.onChange}
        error={hasError}
        helperText={props.helperText ?? message}
        required={isRequired}
      />
    );
  },
);
